/**
 * NfseHistory.js
 * Acordeão colapsável com o histórico de emissões NFS-e canceladas ou com erro.
 * Props:
 *   invoices — array completo de notas fiscais do agendamento
 */

import React, { useState } from 'react';
import { buildFiscalFilename } from '../../../../../api/FiscalAPI';
import { getAccessToken } from '../../../../../api/authTokens';
import { fmtDateTime } from './paymentHelpers';
import { DlIcon, ChevDownIcon, ChevUpIcon } from './PaymentSectionIcons';
import styles from './AppointmentDetailPanel.module.css';

const HIST_COLORS = {
  canceled:   { label: 'Cancelada',        bg: '#f3f4f6', color: '#6b7280' },
  error:      { label: 'Erro',             bg: '#fef2f2', color: '#ef4444' },
  issued:     { label: 'Emitida',          bg: '#f0fdf4', color: '#16a34a' },
  processing: { label: 'Em processamento', bg: '#eff6ff', color: '#3b82f6' },
  queued:     { label: 'Na fila',          bg: '#fffbeb', color: '#f59e0b' },
};

function downloadFile(invoice, type) {
  const token    = getAccessToken();
  const url      = `/api/fiscal/download_fiscal_file.php?invoiceId=${invoice.id}&type=${type}`;
  const filename = buildFiscalFilename(invoice, type);
  fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error('Arquivo não disponível.'); return r.blob(); })
    .then(blob => {
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(u);
    })
    .catch(() => {});
}

export default function NfseHistory({ invoices }) {
  const [expanded, setExpanded] = useState(false);

  const historical = (invoices || []).filter(inv => ['canceled', 'error'].includes(inv.status));
  if (historical.length === 0) return null;

  return (
    <div className={styles.nfseHistory}>
      <button type="button" className={styles.nfseHistoryToggle}
        onClick={() => setExpanded(p => !p)}>
        <span className={styles.nfseHistoryTitle}>
          Histórico de emissões ({historical.length})
        </span>
        {expanded ? <ChevUpIcon /> : <ChevDownIcon />}
      </button>

      {expanded && (
        <div className={styles.nfseHistoryList}>
          {historical.map(inv => {
            const hcfg = HIST_COLORS[inv.status] || HIST_COLORS.canceled;
            const eventDate = inv.canceled_at
              ? fmtDateTime(inv.canceled_at)
              : fmtDateTime(inv.issued_at || inv.created_at);
            return (
              <div key={inv.id} className={styles.nfseHistoryItem}>
                <div className={styles.nfseHistoryItemMain}>
                  <span
                    className={styles.nfseHistoryTag}
                    style={{ background: hcfg.bg, color: hcfg.color }}
                  >
                    {hcfg.label}
                  </span>
                  {inv.amount != null && (
                    <span className={styles.nfseHistoryAmount}>
                      R$ {parseFloat(inv.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  )}
                  {eventDate && (
                    <span className={styles.nfseHistoryDate}>{eventDate}</span>
                  )}
                </div>
                {inv.invoice_number && (
                  <div className={styles.nfseHistoryNfseNum}>NFS-e nº {inv.invoice_number}</div>
                )}
                {inv.error_message && (
                  <div className={styles.nfseHistoryError}>{inv.error_message}</div>
                )}
                {(inv.has_pdf || inv.has_xml) && (
                  <div className={styles.nfseHistoryActions}>
                    {inv.has_pdf && (
                      <button type="button" className={styles.nfseActionBtn}
                        onClick={() => downloadFile(inv, 'pdf')}>
                        <DlIcon /><span>PDF</span>
                      </button>
                    )}
                    {inv.has_xml && (
                      <button type="button" className={styles.nfseActionBtn}
                        onClick={() => downloadFile(inv, 'xml')}>
                        <DlIcon /><span>XML</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
